import { type ChangeEvent, useEffect, useState } from "react";
import { Input, Select } from "@rumbledirect/ui";
import { Question, SurveyModel, ValueChangedEvent } from "survey-core";
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRY_OPTIONS,
  type PhoneCountryCode,
} from "../constants/phoneCountries";

export class PhoneNumberQuestionModel extends Question {
  getType() {
    return "phoneNumber";
  }

  get countries(): string[] {
    const fromJson = (this as unknown as { jsonObj?: { countries?: string[] } })
      .jsonObj?.countries;
    return Array.isArray(fromJson) ? fromJson : [];
  }
}

interface PhoneNumberQuestionProps {
  question: PhoneNumberQuestionModel;
}

export const PhoneNumberQuestion = (props: PhoneNumberQuestionProps) => {
  const survey = props.question.survey as SurveyModel;
  const countryKey = `${props.question.name}__country`;

  const [phoneInput, setPhoneInput] = useState<string>(
    props.question.value ?? "",
  );

  const [country, setCountry] = useState<PhoneCountryCode>(
    (survey?.getValue(countryKey) as PhoneCountryCode) ?? DEFAULT_PHONE_COUNTRY,
  );

  // Sync phone input when the survey value changes externally (e.g. E164 normalization after validation)
  useEffect(() => {
    setPhoneInput(props.question.value ?? "");
  }, [props.question.value]);

  // Sync country state from survey data (e.g. on reload from localStorage)
  useEffect(() => {
    if (!survey) return;

    const handleValueChange = (_: SurveyModel, options: ValueChangedEvent) => {
      if (options.name === countryKey) {
        setCountry(
          (options.value as PhoneCountryCode) ?? DEFAULT_PHONE_COUNTRY,
        );
      }
    };

    survey.onValueChanged.add(handleValueChange);
    return () => {
      survey.onValueChanged.remove(handleValueChange);
    };
  }, [survey, countryKey]);

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneInput(value);
    props.question.value = value;
  };

  const handleCountryChange = (value: string) => {
    setCountry(value as PhoneCountryCode);
    survey?.setValue(countryKey, value);
    if (phoneInput) {
      props.question.value = phoneInput;
    }
  };

  const isDisabled =
    props.question.isDisabledAttr || props.question.isReadOnlyAttr;

  const allowedCountries = props.question.countries;
  const countryOptions =
    allowedCountries.length > 0
      ? PHONE_COUNTRY_OPTIONS.filter((c) => allowedCountries.includes(c.value))
      : PHONE_COUNTRY_OPTIONS;

  // Normalize country when stored value isn't in the allowed list
  useEffect(() => {
    if (
      countryOptions.length > 0 &&
      !countryOptions.some((c) => c.value === country)
    ) {
      const normalized = countryOptions[0].value as PhoneCountryCode;
      setCountry(normalized);
      survey?.setValue(countryKey, normalized);
    }
  }, [countryOptions, country, survey, countryKey]);

  const countrySelector = (
    <Select
      value={country}
      onChange={handleCountryChange}
      disabled={isDisabled || countryOptions.length <= 1}
      options={countryOptions.map((c) => ({
        value: c.value,
        label: c.label,
      }))}
      style={{ width: 110 }}
      aria-label="Land selecteren"
    />
  );

  return (
    <Input
      id={props.question.inputId}
      addonBefore={countrySelector}
      disabled={isDisabled}
      readOnly={props.question.isReadOnlyAttr}
      value={phoneInput}
      type="tel"
      autoComplete="tel"
      onChange={handlePhoneChange}
      onBlur={(e) => props.question.onBlur(e)}
      onFocus={(e) => props.question.onFocus(e)}
      onClick={props.question.readOnlyBlocker}
      onPointerDown={props.question.readOnlyBlocker}
      onKeyUp={props.question.onKeyUp}
      onKeyDown={props.question.onKeyDown}
      onCompositionUpdate={(event) =>
        props.question.onCompositionUpdate(event.nativeEvent)
      }
      styles={{ root: { fontWeight: 500 } }}
      aria-required={props.question.a11y_input_ariaRequired}
      aria-label={props.question.a11y_input_ariaLabel}
      aria-labelledby={props.question.a11y_input_ariaLabelledBy}
      aria-describedby={props.question.a11y_input_ariaDescribedBy}
      aria-invalid={props.question.a11y_input_ariaInvalid}
      aria-errormessage={props.question.a11y_input_ariaErrormessage}
    />
  );
};
